import { computed, onMounted, Ref, ref } from 'vue';
import { useRoute } from 'vue-router';
import Dexie, { Table } from 'dexie';
import { SearchHistory, SearchType } from '@/types';

interface UseSearchHistoriesReturn {
  fetchLatestSearchHistories: () => Promise<void>;
  createSearchHistory: (query: string) => Promise<void>;
  deleteSearchHistory: (query: string) => Promise<void>;
  searchHistories: Ref<SearchHistory[]>;
}

interface QuotaExceededError extends DOMException {
  name: 'QuotaExceededError';
}

class SearchHistoryDB extends Dexie {
  searchHistories!: Table<SearchHistory, number>;

  constructor(dbName: string) {
    super(dbName);
    this.version(1).stores({
      searchHistories: '++id,createdAt,query',
    });
  }
}

const searchHistoryDBCache = new Map<string, SearchHistoryDB>();

/**
 * 検索履歴DBの接続をdbName単位でsingleton化して返す。
 *
 * useSearchHistories()の呼び出しごとに接続を新規生成すると、ログイン画面などの
 * 高速なmount/unmountで接続が積み上がりGC任せになる。将来スキーマ移行(version(2)等)を
 * 追加した際、旧接続はアップグレードをブロックしうるため、接続はユーザーあたり1本に
 * 固定して再利用する。
 */
export const getSearchHistoryDB = (userId: number | null): SearchHistoryDB => {
  const dbName = `searchHistoryDB-${userId}`;
  const cached = searchHistoryDBCache.get(dbName);
  if (cached) {
    return cached;
  }
  const db = new SearchHistoryDB(dbName);
  searchHistoryDBCache.set(dbName, db);
  return db;
};

/**
 * 検索履歴に関するカスタムフック
 *
 * IndexedDBを使用して検索履歴の 保存 / 取得 / 削除 を行う
 *
 * NOTE: 後々に直近の検索履歴だけでなく、入力値に合わせて部分一致する履歴をリアルタイムで表示する機能を追加する予定なので、データ量を考慮してIndexedDBを使用している
 */
export const useSearchHistories = (
  userId: number | null,
): UseSearchHistoriesReturn => {
  const route = useRoute();

  // IndexedDBの接続はdbName単位でsingleton化して再利用する
  const db = getSearchHistoryDB(userId);

  const MAX_SEARCH_HISTORY_ITEM_COUNTS = 5;
  const searchHistories = ref<SearchHistory[]>([]);
  const USER_DOCUMENT_ROUTE_NAME = ['UserDocument', 'searchUserDocumentDemo'];
  const searchType = computed<SearchType>(() =>
    USER_DOCUMENT_ROUTE_NAME.includes(route.name as string)
      ? 'userDocument'
      : 'openInfo',
  );

  /**
   * 直近の (重複のない) 検索履歴を5件取得する
   */
  const fetchLatestSearchHistories = async (): Promise<void> => {
    try {
      // 作成時に重複するqueryは削除しているため、ここでは重複の考慮が不要
      const results: SearchHistory[] = await db.searchHistories
        .orderBy('createdAt')
        .reverse()
        .limit(MAX_SEARCH_HISTORY_ITEM_COUNTS)
        .toArray();

      searchHistories.value = results;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('fetchLatestSearchHistories error: ', error);
    }
  };

  /**
   * 検索実行時に検索履歴を作成する
   */
  const createSearchHistory = async (query: string): Promise<void> => {
    const addSearchHistory = async () => {
      await db.transaction('rw', db.searchHistories, async () => {
        await deleteSearchHistory(query);
        await db.searchHistories.add({
          query,
          createdAt: new Date().toISOString(),
          searchType: searchType.value,
        });
      });
    };

    try {
      await addSearchHistory();
    } catch (error) {
      if (isQuotaExceededError(error)) {
        await cleanupSearchHistories(0.5);
        try {
          await addSearchHistory();
        } catch (retryError) {
          // eslint-disable-next-line no-console
          console.error('addSearchHistory retryError: ', retryError);
        }
      } else {
        // eslint-disable-next-line no-console
        console.error('addSearchHistory error: ', error);
      }
    }
  };

  /**
   * 同じqueryを持つ全ての履歴を削除する
   */
  const deleteSearchHistory = async (query: string): Promise<void> => {
    try {
      await db.searchHistories.where('query').equals(query).delete();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('deleteSearchHistory error: ', error);
    }
  };

  /**
   * オリジンがクォータを使い切った場合の削除処理
   * @param deleteRatio IndexedDBの削除量を割合で指定 (0.0 ~ 1.0)
   *
   * WARN: IndexedDBのデータを指定した割合の数で消してしまうので、無闇に使わないこと
   * INFO: IndexedDBの上限については右記を参照 => https://developer.mozilla.org/ja/docs/Web/API/Storage_API/Storage_quotas_and_eviction_criteria#どれだけのデータが格納できるか
   */
  const cleanupSearchHistories = async (deleteRatio: number): Promise<void> => {
    try {
      await db.transaction('rw', db.searchHistories, async () => {
        const totalItems = await db.searchHistories.count();
        const deleteCount = Math.ceil(totalItems * deleteRatio);
        const itemsToDelete = await db.searchHistories
          .orderBy('createdAt')
          .limit(deleteCount)
          .primaryKeys();

        await db.searchHistories.bulkDelete(itemsToDelete);
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('cleanupSearchHistories error: ', error);
    }
  };

  const isQuotaExceededError = (
    error: unknown,
  ): error is QuotaExceededError => {
    if (error instanceof Dexie.DexieError) {
      return (error.inner as DOMException)?.name === 'QuotaExceededError';
    }
    return (error as QuotaExceededError).name === 'QuotaExceededError';
  };

  onMounted(async () => {
    await fetchLatestSearchHistories();
  });

  // onUnmountedでdb.close()しない。open完了前のunmountでclose()がDatabaseClosedErrorを同期throwし、
  // Sentryへノイズを出していた(STOCKMARK-5ZD)。接続はsingletonで再利用するため、mount/unmountで積み上がらない。

  return {
    fetchLatestSearchHistories,
    createSearchHistory,
    deleteSearchHistory,
    searchHistories,
  };
};
